import React, { useEffect, useRef, useState } from 'react';
import { MapPin, Star, TrendingUp, TrendingDown, Users, Briefcase, BookOpen, Crown } from 'lucide-react';
import { formatMWK } from './tokens';

/**
 * Screen 3 — large slide-up overlay shown when a new ride request lands.
 * Designed for instant decision: huge fare, profitability badge, mini route
 * thumbnail, rider trust info, big Accept / smaller Decline.
 *
 * Props:
 *  ride:       { id, estimated_price, estimated_duration_minutes, estimated_distance_km,
 *                pickup_distance_km, pickup_eta_min, vehicle_type,
 *                pickup_location:{address, latitude, longitude},
 *                dropoff_location:{address, latitude, longitude},
 *                family_id, business_id,
 *                rider_summary?:{rating, trip_count, tip_streak} }
 *  countdown:  initial seconds (default 20)
 *  onAccept / onDecline / onTimeout
 */
const TripRequestCard = ({ ride, countdown = 20, onAccept, onDecline, onTimeout }) => {
  const [secs, setSecs] = useState(countdown);
  const [accepting, setAccepting] = useState(false);
  const timerRef = useRef(null);

  useEffect(() => {
    timerRef.current = setInterval(() => {
      setSecs((s) => {
        if (s <= 1) {
          clearInterval(timerRef.current);
          onTimeout?.();
          return 0;
        }
        return s - 1;
      });
    }, 1000);
    return () => clearInterval(timerRef.current);
  }, [onTimeout]);

  if (!ride) return null;

  const fare = ride.estimated_price ?? ride.fare ?? 0;
  const distanceKm = ride.estimated_distance_km ?? 0;
  const durationMin = ride.estimated_duration_minutes ?? 0;
  const pickupDistance = ride.pickup_distance_km ?? null;
  const pickupEta = ride.pickup_eta_min ?? null;

  // Profitability heuristic: MWK per km vs. global benchmark (MWK 600/km)
  const mwkPerKm = distanceKm > 0 ? fare / distanceKm : 0;
  const isGood = mwkPerKm >= 600;

  const tripBadge = ride.family_id
    ? { label: 'Family ride', Icon: Users, tone: 'bg-rose-100 text-rose-700' }
    : ride.business_id
      ? { label: 'Business', Icon: Briefcase, tone: 'bg-indigo-100 text-indigo-700' }
      : ride.vehicle_type === 'school'
        ? { label: 'School ride', Icon: BookOpen, tone: 'bg-amber-100 text-amber-700' }
        : ride.vehicle_type === 'premium'
          ? { label: 'Premium', Icon: Crown, tone: 'bg-violet-100 text-violet-700' }
          : { label: 'Standard', Icon: null, tone: 'bg-gray-100 text-gray-700' };

  const rs = ride.rider_summary || {};
  const rating = rs.rating ?? 4.8;
  const tripCount = rs.trip_count ?? null;
  const tipsOften = rs.tips_often === true;

  const ringSize = 56;
  const radius = 24;
  const circ = 2 * Math.PI * radius;
  const dash = circ * (1 - secs / countdown);

  const handleAccept = async () => {
    setAccepting(true);
    try { await onAccept?.(ride); } finally {
      // parent unmounts on success; if it doesn't, allow retry
      setAccepting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[80] flex items-end justify-center bg-black/60 backdrop-blur-[2px]" data-testid="trip-request-overlay">
      <div
        className="w-full sm:max-w-md bg-white rounded-t-3xl shadow-[0_-12px_40px_rgba(0,0,0,0.25)] animate-slide-up"
        data-testid="trip-request-card"
        style={{ paddingBottom: 'env(safe-area-inset-bottom, 16px)' }}
      >
        {/* Header: trip badge + countdown ring */}
        <div className="flex items-start justify-between p-4 pb-2">
          <span
            data-testid="trip-request-badge"
            className={`inline-flex items-center gap-1.5 px-2.5 h-7 rounded-full text-[11px] font-bold uppercase tracking-wide ${tripBadge.tone}`}
          >
            {tripBadge.Icon ? <tripBadge.Icon className="w-3 h-3" /> : null}
            {tripBadge.label}
          </span>

          <div className="relative" style={{ width: ringSize, height: ringSize }} data-testid="trip-request-countdown">
            <svg width={ringSize} height={ringSize} className="-rotate-90">
              <circle cx={ringSize / 2} cy={ringSize / 2} r={radius} fill="none" stroke="#E5E7EB" strokeWidth="4" />
              <circle
                cx={ringSize / 2} cy={ringSize / 2} r={radius}
                fill="none"
                stroke={secs <= 5 ? '#DC2626' : '#16A34A'}
                strokeWidth="4"
                strokeDasharray={circ}
                strokeDashoffset={dash}
                strokeLinecap="round"
                style={{ transition: 'stroke-dashoffset 1s linear, stroke 0.2s' }}
              />
            </svg>
            <span className={`absolute inset-0 flex items-center justify-center text-[15px] font-bold tabular-nums ${secs <= 5 ? 'text-red-600' : 'text-gray-900'}`}>
              {secs}
            </span>
          </div>
        </div>

        {/* Row 1: huge fare */}
        <div className="px-5 pt-1 pb-3">
          <div className="flex items-end gap-3">
            <p className="text-[40px] font-bold tracking-tight text-gray-900 leading-none tabular-nums" data-testid="trip-request-fare">
              {formatMWK(fare)}
            </p>
            <span
              data-testid="trip-request-profitability"
              className={`mb-1.5 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold ${
                isGood ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'
              }`}
            >
              {isGood ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
              {isGood ? 'Good value' : 'Low km/MWK'}
            </span>
          </div>
          <p className="text-[13px] text-gray-500 mt-1.5">
            <span className="tabular-nums">~{durationMin} min</span> · <span className="tabular-nums">{distanceKm.toFixed(1)} km</span>
          </p>
        </div>

        {/* Row 2: pickup */}
        <div className="px-5 py-3 border-t border-gray-100 flex items-start gap-3">
          <span className="shrink-0 w-9 h-9 rounded-full bg-emerald-50 text-emerald-600 flex items-center justify-center mt-0.5">
            <MapPin className="w-4 h-4" />
          </span>
          <div className="flex-1 min-w-0">
            <p className="text-[13px] font-semibold text-gray-900">
              {pickupDistance != null ? `${pickupDistance.toFixed(1)} km away` : 'Pickup nearby'}
              {pickupEta != null ? ` · ~${pickupEta} min to pickup` : ''}
            </p>
            <p className="text-[12px] text-gray-500 truncate" data-testid="trip-request-pickup">
              {ride.pickup_location?.address || 'Pickup location'}
            </p>
            <p className="text-[12px] text-gray-500 truncate mt-0.5" data-testid="trip-request-dropoff">
              → {ride.dropoff_location?.address || 'Destination'}
            </p>
          </div>
          <RouteThumb pickup={ride.pickup_location} dropoff={ride.dropoff_location} />
        </div>

        {/* Row 3: rider trust */}
        <div className="px-5 py-3 border-t border-gray-100 flex items-center gap-3">
          <span className="w-9 h-9 rounded-full bg-gray-200 flex items-center justify-center text-gray-500 text-xs font-semibold" aria-hidden>
            R
          </span>
          <div className="flex-1 min-w-0 flex items-center gap-2 flex-wrap">
            <span className="inline-flex items-center gap-1 text-[13px] font-semibold text-gray-900">
              <Star className="w-3.5 h-3.5 text-amber-500 fill-amber-500" /> {Number(rating).toFixed(2)}
            </span>
            {tripCount != null && (
              <span className="text-[12px] text-gray-500">{tripCount} trips</span>
            )}
            {tipsOften && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 text-[11px] font-semibold">
                Tips often
              </span>
            )}
          </div>
        </div>

        {Number(ride.extra_stops || 0) > 0 && (
          <div className="px-5 py-2 border-t border-gray-100 flex items-center gap-2 text-[12px] text-gray-700">
            <span className="inline-flex gap-1">
              {Array.from({ length: Math.min(3, ride.extra_stops) }).map((_, i) => (
                <span key={i} className="w-2 h-2 rounded-full bg-gray-400" />
              ))}
            </span>
            {ride.extra_stops} stop{ride.extra_stops > 1 ? 's' : ''} along the route
          </div>
        )}

        {/* Action buttons */}
        <div className="px-4 pt-4 pb-2">
          <button
            type="button"
            onClick={handleAccept}
            disabled={accepting || secs === 0}
            data-testid="trip-request-accept"
            className="w-full h-14 rounded-full bg-emerald-600 hover:bg-emerald-700 disabled:bg-emerald-300 text-white text-[17px] font-bold tracking-wide active:scale-[0.98] transition-all shadow-lg shadow-emerald-600/30"
          >
            {accepting ? 'Accepting…' : 'Accept'}
          </button>
          <button
            type="button"
            onClick={() => onDecline?.(ride)}
            data-testid="trip-request-decline"
            className="w-full h-11 mt-2 rounded-full text-[14px] font-semibold text-gray-700 hover:bg-gray-50"
          >
            Decline
          </button>
          <p className="text-center text-[11px] text-gray-400 mt-1">No penalty for declining</p>
        </div>
      </div>
    </div>
  );
};

const RouteThumb = ({ pickup, dropoff }) => {
  if (!pickup?.latitude || !dropoff?.latitude) return null;
  // Tiny static SVG thumbnail (no Maps Static call to avoid extra billing).
  return (
    <div className="shrink-0 w-16 h-16 rounded-xl bg-emerald-50 border border-emerald-100 overflow-hidden flex items-center justify-center" aria-hidden>
      <svg viewBox="0 0 64 64" className="w-full h-full">
        <circle cx="14" cy="20" r="4" fill="#16A34A" />
        <circle cx="50" cy="46" r="4" fill="#DC2626" />
        <path d="M14 20 C 24 20, 34 28, 50 46" stroke="#16A34A" strokeWidth="2" fill="none" strokeDasharray="3 3" />
      </svg>
    </div>
  );
};

export default TripRequestCard;
